import i18n from '../i18n';
import { checkoutIntentFromSearch, extractLegacySessionToken, safeAppContinuation } from './authHandoff';
import { apiFetch, getStoredSessionToken, setAuthMode, setStoredSessionToken, isModernAuthEnabled } from './apiClient';

export const authContext = () => ({
  ui_locale: i18n.language,
  inviteToken: new URLSearchParams(window.location.search).get('invite_token') || '',
  checkout_intent: checkoutIntentFromSearch(window.location.search),
});

export async function authRequest(path, body) {
  const response = await apiFetch(path, body === undefined ? {} : {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error('Authentication request failed');
    error.status = response.status;
    error.code = data.code || (response.status === 429 ? 'RATE_LIMITED' : 'AUTH_FAILED');
    throw error;
  }
  return data;
}
export async function loadAuthConfig() {
  const response = await apiFetch('/auth/config');
  // A pre-migration backend has no configuration endpoint.
  if (response.status === 404) { setAuthMode(false); return { enabled: false, googleEnabled: false }; }
  if (!response.ok) throw new Error('Unable to load authentication configuration');
  const data = await response.json();
  if (typeof data.enabled !== 'boolean') throw new Error('Invalid authentication configuration');
  setAuthMode(data.enabled);
  return data;
}

export const sendLoginLink = async (email, inviteToken = '') => {
  const data = await authRequest('/auth/send-login-link', { ...authContext(), email, inviteToken });
  if (data.sessionToken && !isModernAuthEnabled()) setStoredSessionToken(data.sessionToken);
  return data.message;
};
export const loginWithPassword = async (email, password) => {
  const data = await authRequest('/auth/password', { ...authContext(), email, password });
  if (data.sessionToken && !isModernAuthEnabled()) setStoredSessionToken(data.sessionToken);
  return data;
};
const getAuthSession = async () => {
  try { return await authRequest('/auth/session'); }
  catch (error) { if (error.status === 401) return { isAuthenticated: false }; throw error; }
};
export const checkToken = async () => !!(await getAuthSession()).isAuthenticated;

let pendingBootstrap;
let pendingLegacyToken = '';
export function bootstrapAuthSession(config) {
  if (!pendingBootstrap) pendingBootstrap = bootstrapSession(config).finally(() => { pendingBootstrap = undefined; });
  return pendingBootstrap;
}
// Callback/legacy callers only need authentication. App also needs the account
// status from that same response, without a separate onboarding request.
export async function bootstrapAuthentication(config) {
  return !!(await bootstrapAuthSession(config)).isAuthenticated;
}
async function bootstrapSession(config) {
  const { token: urlToken } = extractLegacySessionToken();
  if (urlToken) pendingLegacyToken = urlToken;
  const oldToken = pendingLegacyToken || getStoredSessionToken();
  if (!config.enabled) {
    if (urlToken) setStoredSessionToken(urlToken);
    return getAuthSession();
  }
  const clearLegacyCredential = () => { setStoredSessionToken(''); pendingLegacyToken = ''; };
  const session = await getAuthSession();
  if (session.isAuthenticated) { clearLegacyCredential(); return session; }
  try {
    // The server also supports an eligible legacy HttpOnly cookie when no
    // browser-stored token exists. A failed exchange never authorizes access.
    await authRequest('/auth/exchange-legacy', oldToken ? { token: oldToken } : {});
    clearLegacyCredential();
    return getAuthSession();
  } catch (error) {
    if (error.status === 401) { clearLegacyCredential(); return { isAuthenticated: false }; }
    // A network/service failure must remain retryable. Modern mode never sends
    // this credential as a bearer; it can only reach the explicit exchange.
    throw error;
  }
}

export async function acceptPendingInvitation() {
  const url = new URL(window.location.href);
  const token = url.searchParams.get('invite_token');
  if (!token) return;
  await authRequest('/api/invites/accept', { token });
  url.searchParams.delete('invite_token');
  window.history.replaceState({}, document.title, `${url.pathname}${url.search}`);
}

export async function finishAuthentication(value, { sessionVerified = false } = {}) {
  if (!sessionVerified && !await checkToken()) {
    const error = new Error('Session was not established');
    error.code = 'session_unavailable';
    throw error;
  }
  const destination = safeAppContinuation(value);
  const token = new URL(destination, window.location.origin).searchParams.get('invite_token');
  if (token) {
    await authRequest('/api/invites/accept', { token });
    const url = new URL(destination, window.location.origin);
    url.searchParams.delete('invite_token');
    window.location.replace(`${url.pathname}${url.search}`);
  } else window.location.replace(destination);
}

export const authErrorKey = error => {
  const code = String(error?.code || '').toUpperCase();
  const keys = {
    UNAUTHORIZED: 'sessionExpired', SESSION_UNAVAILABLE: 'sessionUnavailable',
    CREDENTIAL_CHANGED: 'credentialChanged', CONNECTION_CHANGED: 'connectionChanged',
    AUTH_MIGRATION_IN_PROGRESS: 'migrationInProgress', INVALID_EMAIL: 'invalidEmail', INVALID_SIGNUP: 'invalidSignup',
    ORGANIZATION_MEMBERSHIP_CHANGE_REJECTED: 'inviteError',
    INVITE_ACCOUNT_MISMATCH: 'inviteMismatch', ACCOUNT_NOT_LINKED: 'linkRequired',
    LEGACY_SESSION_EXPIRED: 'expired',
    CONNECTION_LINK_EXPIRED: 'expired', CONNECTION_EXPIRED: 'expired', GOOGLE_ACCOUNT_ALREADY_LINKED: 'googleConflict',
    GOOGLE_CANCELLED: 'googleCancelled', GOOGLE_UNAVAILABLE: 'googleUnavailable', GOOGLE_CONNECTION_FAILED: 'googleConnectionFailed',
    INVALID_CREDENTIALS: 'invalidCredentials', INVALID_EMAIL_OR_PASSWORD: 'invalidCredentials',
    EMAIL_NOT_VERIFIED: 'emailNotVerified', INVALID_TOKEN: 'expired', TOKEN_EXPIRED: 'expired',
    TOKEN_USED: 'expired', INVALID_OR_EXPIRED_TOKEN: 'expired', EXPIRED_TOKEN: 'expired',
    PASSWORD_TOO_SHORT: 'passwordLength', PASSWORD_TOO_LONG: 'passwordLength', WEAK_PASSWORD: 'passwordLength',
    RATE_LIMITED: 'rateLimited', TOO_MANY_REQUESTS: 'rateLimited', GOOGLE_NOT_CONFIGURED: 'googleUnavailable',
    GOOGLE_DISABLED: 'googleUnavailable', GOOGLE_ACCOUNT_LINKED: 'googleConflict', ACCOUNT_ALREADY_LINKED: 'googleConflict',
    IDENTITY_CONFLICT: 'googleConflict', ACCOUNT_LINKING_REQUIRED: 'linkRequired', GOOGLE_LINK_REQUIRED: 'linkRequired',
    LAST_SIGN_IN_METHOD: 'lastMethod', LAST_AUTH_METHOD: 'lastMethod', PASSWORD_REQUIRED: 'freshPassword',
    INVALID_PASSWORD: 'invalidCredentials', OAUTH_CANCELLED: 'googleCancelled', ACCESS_DENIED: 'googleCancelled',
    AUTH_DISABLED: 'unavailable', INVITE_INVALID: 'inviteError', INVALID_INVITE: 'inviteError',
  };
  return `authModern.errors.${keys[code] || (error?.status >= 500 ? 'unavailable' : 'generic')}`;
};
